import { useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useStore } from '@/lib/store';
import { syncOnFirstLoad } from '@/services/cloudSync';
import {
  markBootstrapPendingForUser,
  resetSyncLifecycleState,
} from '@/services/syncLifecycle';

const DEFAULT_SMART_SYNC_TIMEOUT_MS = 20_000;

type AuthUserPayload = {
  id: string;
  email: string;
} | null;

type SessionLike = Session | null;

type AuthSubscription = {
  unsubscribe: () => void;
};

type SupabaseAuthLike = {
  getSession: () => Promise<{ data: { session: SessionLike } }>;
  onAuthStateChange: (
    callback: (event: string, session: SessionLike) => void | Promise<void>
  ) => { data: { subscription: AuthSubscription } };
};

type SupabaseClientLike = {
  auth: SupabaseAuthLike;
};

type SessionBootstrapOptions = {
  supabaseClient?: SupabaseClientLike;
  syncOnFirstLoadFn?: (userId: string) => Promise<void>;
  setAuthUser?: (user: AuthUserPayload) => void;
  shouldBlockForSync?: (session: SessionLike) => boolean;
  hydration?: {
    hasHydrated: () => boolean;
    onFinishHydration: (callback: () => void) => () => void;
  };
  syncTimeoutMs?: number;
};

function hasWarmLocalState(): boolean {
  const state = useStore.getState() as {
    athlete?: { name?: string | null };
    plan?: unknown[];
    profiles?: Array<{ id?: string | null }>;
  };

  const athleteName =
    typeof state.athlete?.name === 'string' ? state.athlete.name.trim() : '';
  const hasPlan = Array.isArray(state.plan) && state.plan.length > 0;
  const hasCloudBackedProfile =
    Array.isArray(state.profiles) &&
    state.profiles.some((profile) => {
      if (!profile || typeof profile.id !== 'string') return false;
      return profile.id.length > 0 && !profile.id.startsWith('local-');
    });

  return athleteName.length > 0 || hasPlan || hasCloudBackedProfile;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  if (timeoutMs <= 0) return promise;

  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }) as Promise<T>;
}

import { createLogger } from '@/lib/logger';

const logger = createLogger('useSessionBootstrap');

export function useSessionBootstrap(options: SessionBootstrapOptions = {}) {
  const [loading, setLoading] = useState(true);

  const supabaseClient = options.supabaseClient ?? (supabase as unknown as SupabaseClientLike);
  const syncOnFirstLoadFn = options.syncOnFirstLoadFn ?? syncOnFirstLoad;
  const defaultSetAuthUserRef = useRef(useStore.getState().setAuthUser);
  const defaultShouldBlockForSyncRef = useRef((session: SessionLike) => {
    if (!session) return false;
    return !hasWarmLocalState();
  });
  const defaultHydrationRef = useRef(useStore.persist);
  const setAuthUser = options.setAuthUser ?? defaultSetAuthUserRef.current;
  const shouldBlockForSync =
    options.shouldBlockForSync ?? defaultShouldBlockForSyncRef.current;
  const hydration = options.hydration ?? defaultHydrationRef.current;
  const syncTimeoutMs = options.syncTimeoutMs ?? DEFAULT_SMART_SYNC_TIMEOUT_MS;

  useEffect(() => {
    let isMounted = true;
    let initialSyncDone = false;
    let syncPromise: Promise<void> | null = null;
    let lastSyncedUserId: string | null = null;
    let lastCompletedSyncUserId: string | null = null;

    const setLoadingSafe = (value: boolean) => {
      if (isMounted) setLoading(value);
    };

    const setAuthUserSafe = (session: SessionLike) => {
      if (!isMounted) return;
      if (session) {
        setAuthUser({
          id: session.user.id,
          email: session.user.email || '',
        });
      } else {
        setAuthUser(null);
      }
    };

    const waitForHydration = (): Promise<void> => {
      if (hydration.hasHydrated()) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const unsub = hydration.onFinishHydration(() => {
          unsub();
          resolve();
        });
      });
    };

    const ensureSmartSync = async (userId: string, blockUi: boolean) => {
      if (syncPromise && lastSyncedUserId === userId) {
        await syncPromise;
        return;
      }
      // Supabase can emit multiple SIGNED_IN events for the same active user.
      // Syncing repeatedly remounts route trees and can interrupt in-flight UI requests.
      if (lastCompletedSyncUserId === userId) {
        return;
      }

      if (blockUi) {
        setLoadingSafe(true);
      }
      lastSyncedUserId = userId;
      syncPromise = (async () => {
        await waitForHydration();
        await withTimeout(syncOnFirstLoadFn(userId), syncTimeoutMs, 'Smart sync');
      })();

      try {
        await syncPromise;
        lastCompletedSyncUserId = userId;
      } finally {
        syncPromise = null;
      }
    };

    supabaseClient.auth.getSession().then(async ({ data: { session: currentSession } }) => {
      try {
        // Wait for persisted state to finish hydrating before resolving auth.
        // Otherwise the persist `merge` step can overwrite our authoritative
        // setAuthUser(null) with a stale persisted authUser, which would make
        // PublicRoute redirect "/" to "/dashboard" and trigger the homepage
        // flicker between Dashboard and Onboarding.
        await waitForHydration();
        if (currentSession) {
          markBootstrapPendingForUser(currentSession.user.id);
          const blockUiForSync = shouldBlockForSync(currentSession);
          setAuthUserSafe(currentSession);
          if (blockUiForSync) {
            await ensureSmartSync(currentSession.user.id, true);
          } else {
            void ensureSmartSync(currentSession.user.id, false).catch((err) => {
              logger.error('session', 'Initial session sync failed (background)', { error: err });
            });
          }
        } else {
          // Treat the Supabase session as the source of truth. Stale locally-cached
          // auth placeholders can make the app look logged in even though nothing
          // can be persisted across browser sessions.
          setAuthUserSafe(null);
        }
      } catch (err) {
        logger.error('session', 'Initial session sync failed', { error: err });
      } finally {
        initialSyncDone = true;
        setLoadingSafe(false);
        sessionStorage.setItem('ae_session_active', 'true');
      }
    }).catch((err) => {
      logger.error('session', 'Failed to fetch session', { error: err });
      initialSyncDone = true;
      setLoadingSafe(false);
    });

    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(async (event, session) => {
      if (session) {
        if (event === 'SIGNED_IN') {
          markBootstrapPendingForUser(session.user.id);
        }
        const blockUiForSync = event === 'SIGNED_IN' ? shouldBlockForSync(session) : false;
        setAuthUserSafe(session);
        if (event === 'SIGNED_IN') {
          if (blockUiForSync) {
            try {
              await ensureSmartSync(session.user.id, true);
            } catch (err) {
              logger.error('session', 'Post-login sync failed', { error: err });
            } finally {
              setLoadingSafe(false);
            }
          } else {
            void ensureSmartSync(session.user.id, false).catch((err) => {
              logger.error('session', 'Post-login sync failed (background)', { error: err });
            });
          }
          return;
        }
      } else if (event === 'SIGNED_OUT') {
        // Only clear authUser on explicit sign-out.
        // When there is no session but the user signed up during onboarding
        // (email verification pending), we preserve the locally-stored authUser.
        setAuthUserSafe(null);
        resetSyncLifecycleState();
        lastSyncedUserId = null;
        lastCompletedSyncUserId = null;
        syncPromise = null;
      }

      if (initialSyncDone) {
        setLoadingSafe(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [
    hydration,
    shouldBlockForSync,
    supabaseClient,
    setAuthUser,
    syncOnFirstLoadFn,
    syncTimeoutMs,
  ]);

  return loading;
}
